import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { getClassLevels, getEnrolledStudents, getUnenrolledStudents, enrollStudent, unenrollStudent } from '../api/api';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Snackbar, TextField, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Clear as ClearIcon } from '@mui/icons-material';
import AddStudentDialog from '../components/AddStudentDialog';
import EditStudentDialog from '../components/EditStudentDialog';
import { Student } from '../types';

interface Course {
  id: number;
  title: string;
  class_level?: string;
}

const EnrollmentManagement: React.FC = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<number | ''>('');
  const [classLevels, setClassLevels] = useState<string[]>([]);
  const [selectedClassLevel, setSelectedClassLevel] = useState<string>('');
  const [enrolled, setEnrolled] = useState<Student[]>([]);
  const [unenrolled, setUnenrolled] = useState<Student[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const [isLoadingClassLevels, setIsLoadingClassLevels] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [confirmUnenroll, setConfirmUnenroll] = useState<{ open: boolean; subject: Student | null }>({ open: false, subject: null });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editSubject, setEditSubject] = useState<Student | null>(null);

  const getRowId = useCallback((row: any) => {
    // Defensive check for dummy rows created by the table library
    if (row && row.id) {
      return row.id.toString();
    }
    // Return a random key for any invalid or dummy rows to prevent crashing.
    return Math.random().toString();
  }, []);

  useEffect(() => {
    if (!user) return;
    setIsLoadingCourses(true);
                api.get('/courses/', { params: { school_id: user?.school_id } })
      .then(res => {
        setCourses(res.data as Course[]);
        setIsLoadingCourses(false);
      })
      .catch(() => {
        setIsError(true);
        setIsLoadingCourses(false);
      });
    
    setIsLoadingClassLevels(true);
    getClassLevels()
      .then(res => {
        setClassLevels(res.data as string[]);
        setIsLoadingClassLevels(false);
      })
      .catch(() => {
        setIsError(true);
        setIsLoadingClassLevels(false);
      });
  }, [user?.school_id]);

  // Reset class level filter when course changes
  useEffect(() => {
    setSelectedClassLevel('');
  }, [selectedCourse]);

  const refreshLists = () => {
    if (selectedCourse !== '') {
      setIsLoading(true);
      console.log('Refreshing lists with:', { selectedCourse, selectedClassLevel });
      Promise.all([
        getEnrolledStudents(selectedCourse, selectedClassLevel || undefined),
        getUnenrolledStudents(selectedCourse, selectedClassLevel || undefined)
      ]).then(([enrolledRes, unenrolledRes]) => {
        console.log('API responses:', { 
          enrolled: enrolledRes.data, 
          unenrolled: unenrolledRes.data,
          enrolledCount: (enrolledRes.data as any[])?.length,
          unenrolledCount: (unenrolledRes.data as any[])?.length
        });
        setEnrolled(Array.isArray(enrolledRes.data) ? enrolledRes.data : []);
        setUnenrolled(Array.isArray(unenrolledRes.data) ? unenrolledRes.data : []);
      })
      .catch((error) => {
        console.error('API error:', error);
        setIsError(true);
      })
      .finally(() => setIsLoading(false));
    } else {
        setEnrolled([]);
        setUnenrolled([]);
        setIsLoading(false);
    }
  };
  
  useEffect(() => {
    setIsLoading(!!selectedCourse);
    refreshLists();
  }, [selectedCourse, selectedClassLevel]);

  const safeEnrolled = useMemo(() => enrolled.filter(s => s && s.id), [enrolled]);
  const safeUnenrolled = useMemo(() => unenrolled.filter(s => s && s.id), [unenrolled]);

  const handleEnroll = async (subjectIds: string[]) => {
    if (selectedCourse === '') return;
    await Promise.all(subjectIds.map(id => enrollStudent(Number(id), selectedCourse)));
    setSnackbar({ open: true, message: `Enrolled ${subjectIds.length} student(s)` });
    refreshLists();
  };

  const handleUnenroll = async (subjectIds: string[]) => {
    if (selectedCourse === '') return;
    await Promise.all(subjectIds.map(id => unenrollStudent(Number(id), selectedCourse)));
    setSnackbar({ open: true, message: `Unenrolled ${subjectIds.length} student(s)` });
    refreshLists();
  };

  const columns = useMemo<MRT_ColumnDef<Student>[]>(
    () => [
      { accessorKey: 'id', header: 'ID', size: 40 },
      { 
        accessorKey: 'profile_picture', 
        header: '',
        size: 40,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue<string>() ? <img src={cell.getValue<string>()} alt="profile" style={{ width: 32, height: 32, borderRadius: '50%' }} /> : null,
      },
      { accessorKey: 'name', header: 'Name' },
      { accessorKey: 'class_level', header: 'Class Level' },
    ],
    [],
  );

  if (!user) return <div/>;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Student Enrollment</Typography>
        <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            disabled={!selectedCourse}
        >
            Add New Student
        </Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          select
          label="Course"
          value={selectedCourse}
          onChange={e => setSelectedCourse(Number(e.target.value) || '')}
          SelectProps={{ native: true }}
          sx={{ minWidth: 180 }}
          disabled={isLoadingCourses}
        >
          <option value=''>Select a course</option>
          {courses.map(course => (
            <option key={course.id} value={course.id}>{course.title}{course.class_level ? ` (${course.class_level})` : ''}</option>
          ))}
        </TextField>
        <TextField
          select
          label={selectedClassLevel ? `Filter by Class: ${selectedClassLevel}` : "Filter by Class"}
          value={selectedClassLevel}
          onChange={e => setSelectedClassLevel(e.target.value)}
          SelectProps={{ native: true }}
          sx={{ minWidth: 180 }}
          disabled={!selectedCourse || isLoadingClassLevels}
        >
          <option value=''>All</option>
          {classLevels.map(level => (
            <option key={level} value={level}>{level}</option>
          ))}
        </TextField>
        {selectedClassLevel && (
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={() => setSelectedClassLevel('')}
            disabled={!selectedCourse}
          >
            Clear Filter
          </Button>
        )}
      </Box>

      {!selectedCourse ? (
        <Typography sx={{ mt: 4, textAlign: 'center' }}>Please select a course to manage student enrollments.</Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 4, mt: 3 }}>
          <MaterialReactTable
            columns={columns}
            data={safeEnrolled}
            getRowId={getRowId}
            enableRowActions
            enableRowSelection
            enableToolbarInternalActions={false}
            initialState={{ density: 'compact', showColumnFilters: true }}
            muiToolbarAlertBannerProps={isError ? { color: 'error', children: 'Error loading data' } : undefined}
            state={{ isLoading, showAlertBanner: isError }}
            renderRowActions={({ row }) => (
              <Box sx={{ display: 'flex', gap: '1rem' }}>
                <Tooltip title="Edit">
                  <IconButton onClick={() => { setEditSubject(row.original); setEditDialogOpen(true); }}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Unenroll">
                  <IconButton color="error" onClick={() => setConfirmUnenroll({ open: true, subject: row.original })}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            renderTopToolbarCustomActions={({ table }) => (
              <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ mr: 1 }}>Enrolled</Typography>
                <Button
                  color="error"
                  onClick={() => handleUnenroll(table.getSelectedRowModel().flatRows.map(row => row.id))}
                  disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                >
                  Bulk Unenroll
                </Button>
              </Box>
            )}
          />
          <MaterialReactTable
            columns={columns}
            data={safeUnenrolled}
            getRowId={getRowId}
            enableRowSelection
            enableToolbarInternalActions={false}
            initialState={{ density: 'compact', showColumnFilters: true }}
            muiToolbarAlertBannerProps={isError ? { color: 'error', children: 'Error loading data' } : undefined}
            state={{ isLoading, showAlertBanner: isError }}
            renderTopToolbarCustomActions={({ table }) => (
                <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ mr: 1 }}>Unenrolled</Typography>
                    <Button
                        color="primary"
                        onClick={() => handleEnroll(table.getSelectedRowModel().flatRows.map(row => row.id))}
                        disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                    >
                        Bulk Enroll
                    </Button>
                </Box>
            )}
          />
        </Box>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ open: false, message: '' })} message={snackbar.message} />
      <Dialog open={confirmUnenroll.open} onClose={() => setConfirmUnenroll({ open: false, subject: null })}>
        <DialogTitle>Confirm Unenroll</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to unenroll {confirmUnenroll.subject?.name}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnenroll({ open: false, subject: null })}>Cancel</Button>
          <Button color="error" onClick={() => {
            if (confirmUnenroll.subject) handleUnenroll([confirmUnenroll.subject.id.toString()]);
            setConfirmUnenroll({ open: false, subject: null });
          }}>Unenroll</Button>
        </DialogActions>
      </Dialog>
      <AddStudentDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} schoolId={user.school_id ?? 1} onSuccess={refreshLists} />
      {editSubject && <EditStudentDialog open={editDialogOpen} onClose={() => {setEditDialogOpen(false); setEditSubject(null);}} student={editSubject} onSaved={refreshLists} />}
    </Box>
  );
};

export default EnrollmentManagement;